/**
 * resolveBackorderPhotoUrl(url)
 *
 * Backorder photo URLs have historically been stored in several formats:
 *   1. Relative:  `/api/uploads/backorders/<file>`
 *   2. Relative (quote-request flow):  `/static/uploads/quote_requests/<file>`
 *   3. Absolute pointing to old preview host or `sterilemate.com`
 * As environments change, absolute URLs go stale. This helper rewrites every
 * known upload-path to the current `REACT_APP_BACKEND_URL` so images render
 * across environments without a DB migration.
 */
export function resolveBackorderPhotoUrl(url) {
  if (!url || typeof url !== "string") return null;
  const trimmed = url.trim().replace(/[)>,;.]+$/, "");
  if (!trimmed) return null;

  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  // Case 1 — already relative
  if (trimmed.startsWith("/api/")) return `${base}${trimmed}`;
  if (trimmed.startsWith("/static/uploads/")) return `${base}${trimmed}`;
  if (trimmed.startsWith("/uploads/")) return `${base}/api${trimmed}`;

  // Case 2 — absolute URL containing any known upload path segment → rewrite host
  const markers = [
    "/api/uploads/backorders/",
    "/static/uploads/quote_requests/",
    "/static/uploads/",
    "/api/static/backorders/",
  ];
  for (const marker of markers) {
    const idx = trimmed.indexOf(marker);
    if (idx > -1) return `${base}${trimmed.substring(idx)}`;
  }

  // Case 3 — some unrecognised external URL (rare — e.g. hospital pasted a
  // product page image URL). Return as-is.
  return trimmed;
}

/**
 * extractImageUrlsFromText(text)
 *
 * Pulls image-looking URLs out of a free-form notes string. Used by the
 * admin Backorders drawer to render clickable thumbnails for URLs the
 * customer pasted into the Notes field (e.g. from the QuickQuote
 * Homepage-Form flow which prefixes the uploaded photo URL onto notes).
 */
export function extractImageUrlsFromText(text) {
  if (!text || typeof text !== "string") return [];
  const urlRe = /(https?:\/\/[^\s<>"'`]+|\/(?:api\/)?(?:static\/uploads|uploads)\/[^\s<>"'`]+)/gi;
  const out = [];
  const seen = new Set();
  let m;
  while ((m = urlRe.exec(text)) !== null) {
    const raw = m[1].replace(/[)>,;.]+$/, "");
    // Filter to image-y paths only
    if (
      /\.(?:jpe?g|png|webp|gif|heic|heif)(?:\?|$)/i.test(raw) ||
      /\/uploads\/|\/static\/uploads\//i.test(raw)
    ) {
      if (!seen.has(raw)) { seen.add(raw); out.push(raw); }
    }
  }
  return out;
}
